<template>
  <v-card>
    <v-card-title>{{ t("components.companies.rates.title") }}</v-card-title>
    <v-card-text>
      <v-form v-if="!shouldDisableForDate">
        <h3 class="mx-2">
          {{ t("components.companies.rates.contractDetails") }}
        </h3>
        <v-select
          v-model="pendingDealRates.transactionRates.billingType"
          :items="billingTypeItems"
          :item-title="(item) => item.type"
          :label="t('components.companies.rates.typeLabel')"
          class="mx-2 pt-2"
          min-width="250"
        >
          <template #item="{ props, item }">
            <v-list-item
              v-bind="props"
              :title="item.raw.type"
              :disabled="item.raw.disabled"
              :subtitle="item.raw.hint"
            />
          </template>
        </v-select>
        <div class="d-flex flex-column">
          <h3 class="mx-2">
            {{ t("components.companies.rates.deviceRental") }}
          </h3>
          <NumberInput
            v-model="pendingDealRates.deviceRate"
            :label="t('components.companies.rates.monthlyRateLabel')"
            class="ma-2"
          />
        </div>
        <div class="d-flex flex-column">
          <h3 class="mx-2">
            {{ t("components.companies.rates.settlementRate") }}
          </h3>
          <NumberInput
            v-model="pendingDealRates.settlementRate"
            :label="t('components.companies.rates.settlementRateLabel')"
            class="ma-2"
          />
        </div>
        <h3 class="mx-2">
          {{ t("components.companies.rates.contractEndDate") }}
        </h3>
        <VueDatePicker
          :model-value="pendingDealRates.contractEndDate"
          :locale="locale"
          :cancel-text="t('form.cancel')"
          :select-text="t('form.select')"
          format="dd/MM/yyyy"
          :placeholder="t('components.companies.rates.contractEndDatePlaceholder')"
          :min-date="new Date()"
          :enable-time-picker="false"
          class="px-2 pt-2 pb-6"
          @update:model-value="
            (modelData?: Date) => {
              // need to remove rather than setting to undefined so deep equality check works
              if (modelData) {
                pendingDealRates.contractEndDate = modelData.toISOString();
              } else {
                delete pendingDealRates.contractEndDate;
              }
            }
          "
        />
        <h3 class="mx-2 mt-8">
          {{ t("components.companies.rates.domestic") }}
        </h3>
        <div class="d-flex">
          <NumberInput
            v-model="pendingDealRates.transactionRates.domestic.auth"
            :label="t('components.companies.rates.authLabel')"
            class="ma-2"
          />
          <NumberInput
            v-model="pendingDealRates.transactionRates.domestic.debit"
            :label="t('components.companies.rates.debitLabel')"
            class="ma-2"
          />
          <NumberInput
            v-model="pendingDealRates.transactionRates.domestic.credit"
            :label="t('components.companies.rates.creditLabel')"
            class="ma-2"
          />
          <NumberInput
            v-model="pendingDealRates.transactionRates.domestic.premium"
            :label="t('components.companies.rates.premiumLabel')"
            class="ma-2"
          />
        </div>
        <h3 class="mx-2">
          <!-- terms flipped at yetipay - intra is global -->
          {{ t("components.companies.rates.intra") }}
        </h3>
        <div class="d-flex">
          <NumberInput
            v-model="pendingDealRates.transactionRates.intra.auth"
            :label="t('components.companies.rates.authLabel')"
            class="ma-2"
          />
          <NumberInput
            v-model="pendingDealRates.transactionRates.intra.debit"
            :label="t('components.companies.rates.debitLabel')"
            class="ma-2"
          />
          <NumberInput
            v-model="pendingDealRates.transactionRates.intra.credit"
            :label="t('components.companies.rates.creditLabel')"
            class="ma-2"
          />
          <NumberInput
            v-model="pendingDealRates.transactionRates.intra.premium"
            :label="t('components.companies.rates.premiumLabel')"
            class="ma-2"
          />
        </div>
        <h3 class="mx-2">
          <!-- terms flipped at yetipay - inter is EEA -->
          {{ t("components.companies.rates.inter") }}
        </h3>
        <div class="d-flex">
          <NumberInput
            v-model="pendingDealRates.transactionRates.inter.auth"
            :label="t('components.companies.rates.authLabel')"
            class="ma-2"
          />
          <NumberInput
            v-model="pendingDealRates.transactionRates.inter.debit"
            :label="t('components.companies.rates.debitLabel')"
            class="ma-2"
          />
          <NumberInput
            v-model="pendingDealRates.transactionRates.inter.credit"
            :label="t('components.companies.rates.creditLabel')"
            class="ma-2"
          />
          <NumberInput
            v-model="pendingDealRates.transactionRates.inter.premium"
            :label="t('components.companies.rates.premiumLabel')"
            class="ma-2"
          />
        </div>
        <h3 class="mx-2">
          {{ t("components.companies.rates.amex") }}
        </h3>
        <div class="d-flex">
          <NumberInput
            v-model="pendingDealRates.transactionRates.amex.auth"
            :label="t('components.companies.rates.authLabel')"
            class="ma-2"
          />
          <NumberInput
            v-model="pendingDealRates.transactionRates.amex.card"
            :label="t('components.companies.rates.cardLabel')"
            class="ma-2"
          />
        </div>
        <h3 class="mx-2">
          {{ t("components.companies.rates.other") }}
        </h3>
        <div class="d-flex">
          <NumberInput
            v-model="pendingDealRates.transactionRates.other.auth"
            :label="t('components.companies.rates.authLabel')"
            class="ma-2"
          />
          <NumberInput
            v-model="pendingDealRates.transactionRates.other.card"
            :label="t('components.companies.rates.cardLabel')"
            class="ma-2"
          />
        </div>
        <h3 class="mx-2">
          {{ t("components.companies.rates.tapToPay") }}
        </h3>
        <div class="d-flex">
          <NumberInput
            v-model="pendingDealRates.transactionRates.tapToPay!.auth"
            :label="t('components.companies.rates.authLabel')"
            class="ma-2"
          />
          <NumberInput
            v-model="pendingDealRates.transactionRates.tapToPay!.ios"
            label="iOS"
            class="ma-2"
          />
        </div>
        <div class="d-flex justify-end">
          <v-checkbox
            v-if="hasPendingChanges"
            v-model="shouldBackfill"
            :label="t('components.companies.rates.backfillLabel')"
            inset
            color="primary"
            class="mx-4"
          />
          <v-btn :disabled="!hasPendingChanges" :loading="isLoading" @click="update">
            {{ t("components.companies.rates.saveButton") }}
          </v-btn>
        </div>
      </v-form>
      <v-alert v-else type="error">
        {{ t("components.companies.rates.updateError") }}
      </v-alert>
    </v-card-text>
  </v-card>
</template>

<script setup lang="ts">
import {
  DealRates,
  TransactionRatesBillingTypeEnum,
  MerchantUpdateRatesRequest,
} from "@tableyeti/merchant-service";
import { isLastDayOfMonth } from "date-fns";
import _, { pick } from "lodash";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";

const { t, locale } = useI18n();

const { dealRates } = defineProps<{ dealRates: DealRates; isLoading: boolean }>();
const emit = defineEmits<{
  update: [rates: MerchantUpdateRatesRequest];
}>();

const pendingDealRates = ref<DealRates>({
  ...dealRates,
  transactionRates: {
    ...dealRates.transactionRates,
    tapToPay: {
      auth: dealRates.transactionRates.tapToPay?.auth || 0.08,
      ios: dealRates.transactionRates.tapToPay?.ios || 1.8,
      android: dealRates.transactionRates.tapToPay?.android || 1.8,
    },
  },
});
console.log(pendingDealRates.value);

const shouldBackfill = ref(true);
const shouldDisableForDate = ref(isLastDayOfMonth(new Date()));
const hasPendingChanges = computed(() => !_.isEqual(dealRates, pendingDealRates.value));
const billingTypeItems = computed(() => {
  return Object.values(TransactionRatesBillingTypeEnum).map((value) => {
    let disabled: boolean;
    if (dealRates.transactionRates.billingType == TransactionRatesBillingTypeEnum.Ic) {
      disabled = value != TransactionRatesBillingTypeEnum.Ic;
    } else {
      disabled = value == TransactionRatesBillingTypeEnum.Ic;
    }
    return {
      disabled,
      type: value,
      hint: disabled
        ? `Unable to change from "${dealRates.transactionRates.billingType}" to "${value}" using this tool`
        : undefined,
    };
  });
});

const update = () => {
  const body = {
    ...pick(
      pendingDealRates.value,
      "transactionRates",
      "discounts",
      "contractEndDate",
      "deviceRate",
      "settlementRate",
    ),
    shouldBackfill: shouldBackfill.value,
  };
  emit("update", body);
};
</script>
